/** Copyright (c) 2018 T.Rowe Price. All rights reserved. */

import './NewExternalInvestmentProductCard.scss';
import { isNil } from 'lodash';
import { connect } from 'react-redux';
import React, { Fragment, useState } from 'react';

import { ReactComponent as AlertCircleIcon } from 'assets/icons/alert-circle.svg';
import ConsentedClient from './ConsentedClient';
import ProspectiveClient from './ProspectiveClient';
import { getActiveVendorSessions } from 'state/session/SessionSelectors';
import AccountLayoutHeader from 'components/layouts/AccountLayoutHeader/AccountLayoutHeader';
import ExternalModal from 'components/shared/Modal/ExternalModal';
import {
  isProspectiveClient,
  hasCompletedRegistrationTasks
} from 'state/external/ExternalInvestmentConsentTypes';
import {
  getExternalInvestmentError,
  getExternalInvestmentConsent,
  getExternalInvestmentConsentStatus,
  getShowConsentModal,
  getShowLinkModal
} from 'state/external/ExternalInvestmentConsentSelectors';
import { getExternalInvestmentsBalances } from 'state/external/ExternalInvestmentsBalancesSelectors';
import { showLinkModal } from 'state/external/ExternalInvestmentConsentActions';
import WithUserPermissions from 'components/shared/WithUserPermissions/WithUserPermissions';
import ProductCard from 'components/shared/ProductCard/ProductCard';
import { Spinner } from '@trp-beacon-ui-kit/react-communication';
import If from 'components/shared/If/If';
import Content from 'services/Content';
import Store from 'state/Store';
import {
  expireVendorSession,
  initializeVendorSession
} from 'state/session/SessionActions';
import { getCustomerWebView } from 'state/customer/CustomerSelectors';
import { getHasReceivedCustomer } from 'state/requests/RequestStatusSelectors';

import {
  getExternalInvestmentBalances,
  refreshExternalInvestmentBalances
} from 'services/Rest';

import {
  accountCenterLinks,
  linkBuilder,
  externalInvestmentLinks
} from 'services/Externals';

export const activateExternalAccountSession = () => {
  Store.dispatch(
    initializeVendorSession({ name: 'externalAccount', active: true })
  );
};

/* Look to do this in the reducer */
export const hasActiveExternalAccountSession = (activeVendorSessions) => {
  const activeVendorSession = activeVendorSessions.filter(
    (s) => s.name === 'externalAccount' && s.active
  );
  return Boolean(activeVendorSession.length);
};

const handleLinkAccountClose = (activeVendorSessions, consent) => {
  if (hasActiveExternalAccountSession(activeVendorSessions)) {
    Store.dispatch(expireVendorSession('externalAccount'));
  }
  if (consent) {
    refreshExternalInvestmentBalances();
  }
};

const ExternalInvestmentError = () => (
  <span>
    <AlertCircleIcon className="external-investment-card-error-image" />
    Error Loading External Accounts
  </span>
);

const ExternalLinkButton = ({ href, handleClick, label }) => {
  if (href) {
    return (
      <a className="external-link-button no-underline" href={href}>
        {label}
      </a>
    );
  }
  return (
    <a className="external-link-button" onClick={handleClick}>
      {label}
    </a>
  );
};

const ExternalLinkButtonWithPermissions =
  WithUserPermissions(ExternalLinkButton);

const PageLoadingSpinner = () => (
  <div className="loading-spinner-overlay">
    <Spinner />
  </div>
);

const NewExternalInvestmentProductCard = ({
  activeVendorSessions,
  consentStatus,
  consent,
  hasError,
  isLoading,
  showLinkAccountModal,
  view
}) => {
  const [iframeSource, setIframeSource] = useState('');

  const [autoOpen, setAutoOpen] = useState(null);

  return (
    <Fragment>
      <If test={isLoading}>
        <PageLoadingSpinner />
      </If>
      <div
        className={`external-investment-card-container ${
          isLoading ? 'loading-spinner-overlay' : ''
        }`}>
        <AccountLayoutHeader
          text="External Investment Accounts"
          hideDetailLink={true}
          isSubheader={false}
          className={`external-investment-card-header ${
            hasError ? 'tile-level-error' : ''
          }`}
        />
        <If test={hasError && consent}>
          <div>
            <ProductCard
              className="error-loading-external-accounts"
              title=""
              description={<ExternalInvestmentError />}
              content={Content.externalInvestment.hasError}>
              <div>
                <ExternalLinkButtonWithPermissions
                  handleClick={getExternalInvestmentBalances}
                  label="Try Again"
                  styleOverride={{ display: 'inline' }}
                  shouldDisable
                  view={view}
                />
                <ExternalLinkButtonWithPermissions
                  href={linkBuilder(accountCenterLinks, view)}
                  handleClick={activateExternalAccountSession}
                  shouldDisable
                  styleOverride={{
                    display: 'inline'
                  }}
                  view={view}
                  label="Account Center"
                />
              </div>
            </ProductCard>
          </div>
        </If>
        <If test={hasError && !consent}>
          <div>
            <ProductCard
              className="error-loading-external-accounts"
              title=""
              description={<ExternalInvestmentError />}
              content={Content.externalInvestment.hasError}>
              <div></div>
            </ProductCard>
          </div>
        </If>
        <If test={!hasError}>
          <If
            test={
              !consent || isNil(consent) || isProspectiveClient(consentStatus)
            }>
            <ProspectiveClient setAutoOpen={setAutoOpen} />
          </If>
          <If
            test={
              consent ||
              (hasCompletedRegistrationTasks(consentStatus) &&
                !isProspectiveClient(consentStatus))
            }>
            <ExternalModal
              close={() => {
                setIframeSource(externalInvestmentLinks.logout);
                handleLinkAccountClose(activeVendorSessions);
                Store.dispatch(showLinkModal(false));
              }}
              frameUrl={iframeSource}
              isOpen={consent && showLinkAccountModal}
            />
            <ConsentedClient
              iframeSource={iframeSource}
              autoOpen={autoOpen}
              setIframeSource={setIframeSource}
            />
          </If>
        </If>
      </div>
    </Fragment>
  );
};

const mapStateToProps = (state) => ({
  activeVendorSessions: getActiveVendorSessions(state),
  externalAccounts: getExternalInvestmentsBalances(state),
  isCustomerWebView: getCustomerWebView(state),
  hasReceivedCustomer: getHasReceivedCustomer(state),
  consentStatus: getExternalInvestmentConsentStatus(state),
  consent: getExternalInvestmentConsent(state),
  hasError: getExternalInvestmentError(state),
  showModal: getShowConsentModal(state),
  showLinkAccountModal: getShowLinkModal(state)
});

export default connect(mapStateToProps)(NewExternalInvestmentProductCard);
